import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import { canonicalTeamName } from "./utils/feeRules";
import { TEAM_MEMBERS, normalizeTeamMember } from "./utils/teamMembers";
import "./RoleManager.css";

const MAIN_ADMIN_EMAIL = "notloser.08@gmail.com";

const ROLE_META = {
  admin: { label: "Admin", description: "เข้าถึงและจัดการระบบทั้งหมด" },
  team: { label: "Team", description: "กรอกค่ากดและดูข้อมูลของตัวเอง" },
  report: { label: "Report", description: "ดูรายงานโดยไม่ใช้สิทธิ์แอดมิน" },
  pending: { label: "Pending", description: "รอแอดมินอนุมัติและผูกชื่อทีม" },
  inactive: { label: "Inactive", description: "ปิดใช้งานบัญชีนี้ชั่วคราว" },
};

const normalizeRole = (value) => (ROLE_META[value] ? value : "pending");

export default function RoleManager() {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editingUser, setEditingUser] = useState(null);
  const [draftRole, setDraftRole] = useState("team");
  const [draftTeamName, setDraftTeamName] = useState("");
  const [draftRealName, setDraftRealName] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    const unsubscribeUsers = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        const nextUsers = snapshot.docs.map((userDoc) => ({
          id: userDoc.id,
          ...userDoc.data(),
        }));
        setUsers(nextUsers);
        setLoading(false);
      },
      (error) => {
        console.error("โหลดผู้ใช้ไม่สำเร็จ", error);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeAuth();
      unsubscribeUsers();
    };
  }, []);

  const isMainAdmin = currentUser?.email === MAIN_ADMIN_EMAIL;

  const sortedUsers = useMemo(() => {
    const roleWeight = { pending: 0, admin: 1, team: 2, report: 3, inactive: 4 };
    return [...users].sort((a, b) => {
      const roleDiff = (roleWeight[normalizeRole(a.role)] ?? 9) - (roleWeight[normalizeRole(b.role)] ?? 9);
      if (roleDiff !== 0) return roleDiff;
      const aName = canonicalTeamName(a.teamName) || a.name || a.displayName || a.email || "";
      const bName = canonicalTeamName(b.teamName) || b.name || b.displayName || b.email || "";
      return String(aName).localeCompare(String(bName), "th");
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return sortedUsers.filter((user) => {
      const role = normalizeRole(user.role);
      if (filter !== "all" && role !== filter) return false;
      if (!keyword) return true;

      const haystack = [
        user.name,
        user.displayName,
        user.email,
        user.realName,
        canonicalTeamName(user.teamName),
        role,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(keyword);
    });
  }, [sortedUsers, filter, search]);

  const stats = useMemo(() => {
    const result = { total: users.length, admin: 0, team: 0, report: 0, pending: 0, inactive: 0, unlinked: 0, missingRealName: 0 };
    users.forEach((user) => {
      const role = normalizeRole(user.role);
      result[role] += 1;
      if (role === "team" && !normalizeTeamMember(user.teamName)) result.unlinked += 1;
      if (role === "team" && !String(user.realName || "").trim()) result.missingRealName += 1;
    });
    return result;
  }, [users]);

  const usedTeamNames = useMemo(() => {
    const set = new Set();
    users.forEach((user) => {
      const role = normalizeRole(user.role);
      const team = normalizeTeamMember(user.teamName);
      if (team && role !== 'inactive') set.add(team);
    });
    return set;
  }, [users]);

  const availableTeamMembers = useMemo(() => {
    const current = editingUser ? normalizeTeamMember(editingUser.teamName) : '';
    return TEAM_MEMBERS.filter((name) => name === current || !usedTeamNames.has(name));
  }, [editingUser, usedTeamNames]);

  const openEditor = (user) => {
    setEditingUser(user);
    setDraftRole(normalizeRole(user.role));
    setDraftTeamName(normalizeTeamMember(user.teamName));
    setDraftRealName(String(user.realName || "").trim());
    setConfirming(false);
    setNotice("");
  };

  const closeEditor = () => {
    if (saving) return;
    setEditingUser(null);
    setConfirming(false);
  };

  const roleChanged = editingUser && draftRole !== normalizeRole(editingUser.role);
  const originalTeamName = editingUser ? normalizeTeamMember(editingUser.teamName) : "";
  const teamNameChanged = editingUser && draftTeamName !== originalTeamName;
  const originalRealName = editingUser ? String(editingUser.realName || "").trim() : "";
  const realNameChanged = editingUser && draftRealName.trim() !== originalRealName;
  const hasChanges = Boolean(roleChanged || teamNameChanged || realNameChanged);
  const editingSelf = editingUser?.id === currentUser?.uid;

  const requestSave = () => {
    if (!hasChanges || saving) return;

    // บัญชีหลักไม่ควรถูกลดสิทธิ์จากหน้าเว็บโดยเผลอกดผิด
    if (editingSelf && roleChanged) {
      setNotice("บัญชีที่กำลังใช้งานอยู่ไม่สามารถเปลี่ยน Role ของตัวเองจากหน้านี้ได้");
      return;
    }

    if (draftRole === "team" && !draftTeamName) {
      setNotice("บัญชี Team ต้องผูกชื่อทีมก่อนบันทึก");
      return;
    }

    if (draftTeamName) {
      const duplicate = users.find((user) =>
        user.id !== editingUser?.id &&
        normalizeRole(user.role) !== "inactive" &&
        normalizeTeamMember(user.teamName) === draftTeamName
      );
      if (duplicate) {
        setNotice(`ชื่อทีม ${draftTeamName} ถูกผูกกับ ${duplicate.email || duplicate.name || "บัญชีอื่น"} อยู่แล้ว`);
        return;
      }
    }

    setNotice("");
    setConfirming(true);
  };

  const saveChanges = async () => {
    if (!editingUser || !hasChanges || saving) return;
    setSaving(true);
    setNotice("");

    try {
      const updates = {
        updatedAt: serverTimestamp(),
      };

      if (roleChanged) {
        updates.role = draftRole;
        updates.roleUpdatedAt = serverTimestamp();
      }

      const nextTeamName = draftRole === 'inactive' ? '' : draftTeamName;
      if (teamNameChanged || (draftRole === 'inactive' && originalTeamName)) {
        // ปิดใช้งาน = ปล่อยชื่อทีมกลับเข้าลิสต์ เพื่อเอาไปผูกกับเมลใหม่ได้
        updates.teamName = nextTeamName || '';
        updates.teamNameUpdatedAt = serverTimestamp();
      }

      if (draftRole === 'inactive') {
        updates.deactivatedAt = serverTimestamp();
      } else if (normalizeRole(editingUser.role) === 'inactive') {
        updates.reactivatedAt = serverTimestamp();
      }

      if (realNameChanged) {
        updates.realName = draftRealName.trim();
        updates.realNameUpdatedAt = serverTimestamp();
      }

      await updateDoc(doc(db, "users", editingUser.id), updates);
      setNotice("บันทึกข้อมูลผู้ใช้เรียบร้อยแล้ว");
      setConfirming(false);

      setTimeout(() => {
        setEditingUser(null);
        setNotice("");
      }, 650);
    } catch (error) {
      console.error("อัปเดตผู้ใช้ไม่สำเร็จ", error);
      setNotice("บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const displayName = (user) => user.name || user.displayName || "ไม่ระบุชื่อ";
  const linkedTeamName = (user) => normalizeTeamMember(user.teamName);

  return (
    <div className="role-page">
      <section className="role-hero">
        <div>
          <span className="role-eyebrow">USER ACCESS</span>
          <h1>จัดการผู้ใช้</h1>
          <p>ตรวจสอบบัญชี อนุมัติผู้ใช้ กำหนดสิทธิ์ และผูกชื่อทีมกับอีเมลจากหน้าเดียว</p>
        </div>
        <div className={`role-permission ${isMainAdmin ? "can-edit" : "read-only"}`}>
          <span>{isMainAdmin ? "แก้ไขได้" : "ดูอย่างเดียว"}</span>
          <strong>{isMainAdmin ? "บัญชีหลัก" : "Admin"}</strong>
        </div>
      </section>

      <section className="role-stats">
        <article>
          <span>ผู้ใช้ทั้งหมด</span>
          <strong>{stats.total}</strong>
          <small>บัญชี</small>
        </article>
        <article>
          <span>Admin</span>
          <strong>{stats.admin}</strong>
          <small>จัดการระบบ</small>
        </article>
        <article>
          <span>Team</span>
          <strong>{stats.team}</strong>
          <small>กรอกค่ากด</small>
        </article>
        <article>
          <span>Report</span>
          <strong>{stats.report}</strong>
          <small>ดูรายงาน</small>
        </article>
        <article className={stats.pending > 0 || stats.unlinked > 0 || stats.missingRealName > 0 ? "needs-attention" : ""}>
          <span>ต้องตรวจสอบ</span>
          <strong>{stats.pending + stats.unlinked + stats.missingRealName}</strong>
          <small>{stats.pending > 0 || stats.unlinked > 0 || stats.missingRealName > 0 ? `รออนุมัติ ${stats.pending} · ยังไม่ผูกทีม ${stats.unlinked} · ไม่มีชื่อจริง ${stats.missingRealName}` : "เรียบร้อย"}</small>
        </article>
      </section>

      <section className="role-panel">
        <div className="role-toolbar">
          <div className="role-search-wrap">
            <span>⌕</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="ค้นหาชื่อจริง ชื่อทีม หรืออีเมล"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} aria-label="ล้างการค้นหา">×</button>
            )}
          </div>

          <div className="role-filter-tabs">
            {[
              ["all", "ทั้งหมด", stats.total],
              ["pending", "รออนุมัติ", stats.pending],
              ["admin", "Admin", stats.admin],
              ["team", "Team", stats.team],
              ["report", "Report", stats.report],
              ["inactive", "ปิดใช้งาน", stats.inactive],
            ].map(([key, label, count]) => (
              <button
                type="button"
                key={key}
                className={filter === key ? "active" : ""}
                onClick={() => setFilter(key)}
              >
                {label}<span>{count}</span>
              </button>
            ))}
          </div>
        </div>

        {!isMainAdmin && (
          <div className="role-info-banner">
            บัญชีนี้สามารถตรวจสอบข้อมูลผู้ใช้ได้ แต่การอนุมัติ เปลี่ยน Role ชื่อทีม และชื่อจริงทำได้จากบัญชีหลักเท่านั้น
          </div>
        )}

        {loading ? (
          <div className="role-empty">กำลังโหลดผู้ใช้...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="role-empty">
            <strong>ไม่พบผู้ใช้</strong>
            <span>ลองเปลี่ยนคำค้นหาหรือตัวกรอง</span>
          </div>
        ) : (
          <div className="role-user-list">
            {filteredUsers.map((user) => {
              const role = normalizeRole(user.role);
              const team = linkedTeamName(user);
              const isSelf = user.id === currentUser?.uid;
              const isMainAccount = user.email === MAIN_ADMIN_EMAIL;

              return (
                <article className={`role-user-card ${isSelf ? "is-self" : ""} ${role === 'inactive' ? 'is-inactive' : ''}`} key={user.id}>
                  <div className="role-avatar">
                    {(team || displayName(user) || "?").trim().charAt(0).toUpperCase()}
                  </div>

                  <div className="role-user-main">
                    <div className="role-user-title-row">
                      <h3>{displayName(user)}</h3>
                      {isSelf && <span className="role-you">คุณ</span>}
                      {isMainAccount && <span className="role-main-admin">บัญชีหลัก</span>}
                    </div>
                    {user.realName && <p className="role-real-name">{user.realName}</p>}
                    <p>{user.email || "ไม่มีอีเมล"}</p>
                    <div className="role-user-meta">
                      <span className={`role-badge ${role}`}>{ROLE_META[role].label}</span>
                      <span className={`team-link-badge ${team ? "linked" : "unlinked"}`}>
                        {team ? `ทีม: ${team}` : "ยังไม่ผูกชื่อทีม"}
                      </span>
                    </div>
                  </div>

                  <div className="role-user-action">
                    <span className="role-description">{ROLE_META[role].description}</span>
                    {isMainAdmin ? (
                      <button type="button" onClick={() => openEditor(user)}>
                        แก้ไข
                      </button>
                    ) : (
                      <span className="role-view-only">ดูอย่างเดียว</span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {editingUser && (
        <div className="role-modal-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeEditor();
        }}>
          <div className="role-modal" role="dialog" aria-modal="true" aria-label="แก้ไขผู้ใช้">
            <div className="role-modal-head">
              <div>
                <span className="role-eyebrow">EDIT USER</span>
                <h2>{displayName(editingUser)}</h2>
                <p>{editingUser.email}</p>
              </div>
              <button type="button" onClick={closeEditor} disabled={saving}>×</button>
            </div>

            {!confirming ? (
              <>
                <div className="role-modal-body">
                  <label className="role-field">
                    <span>สิทธิ์การใช้งาน</span>
                    <select
                      value={draftRole}
                      onChange={(event) => {
                        const nextRole = event.target.value;
                        setDraftRole(nextRole);
                        if (nextRole === 'inactive') setDraftTeamName('');
                        setNotice("");
                      }}
                      disabled={editingSelf}
                    >
                      <option value="pending">Pending — รออนุมัติ</option>
                      <option value="admin">Admin — จัดการระบบทั้งหมด</option>
                      <option value="team">Team — กรอกค่ากดของทีม</option>
                      <option value="report">Report — ดูรายงาน</option>
                      <option value="inactive">Inactive — ปิดใช้งานบัญชี</option>
                    </select>
                    {editingSelf && <small>เพื่อกันพลาด ระบบไม่ให้เปลี่ยน Role ของบัญชีที่กำลังล็อกอินอยู่</small>}
                  </label>

                  <label className="role-field">
                    <span>ชื่อจริง</span>
                    <input
                      type="text"
                      value={draftRealName}
                      onChange={(event) => {
                        setDraftRealName(event.target.value);
                        setNotice("");
                      }}
                      placeholder="เช่น ชฎาธารี บัวศรี"
                      maxLength={120}
                    />
                    <small>ใช้สำหรับแสดงคู่กับชื่อทีมในหน้าการเงิน รายงาน และโอนค่ากด ไม่กระทบการคำนวณ</small>
                  </label>


                  <label className="role-field">
                    <span>ชื่อทีมที่ล็อกกับบัญชี</span>
                    <select
                      value={draftTeamName}
                      onChange={(event) => {
                        setDraftTeamName(event.target.value);
                        setNotice("");
                      }}
                    >
                      <option value="">ยังไม่ผูกชื่อทีม</option>
                      {availableTeamMembers.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    <small>รายชื่อที่ถูกผูกกับบัญชีอื่นแล้วจะไม่แสดงในลิสต์ · 1 ทีม = 1 เมล</small>
                  </label>

                  <div className="role-change-preview">
                    <div>
                      <span>Role</span>
                      <strong>{ROLE_META[normalizeRole(editingUser.role)].label}</strong>
                      {roleChanged && <em>→ {ROLE_META[draftRole].label}</em>}
                    </div>
                    <div>
                      <span>ชื่อทีม</span>
                      <strong>{originalTeamName || "ยังไม่ผูก"}</strong>
                      {teamNameChanged && <em>→ {draftTeamName || "ยังไม่ผูก"}</em>}
                    </div>
                    <div>
                      <span>ชื่อจริง</span>
                      <strong>{originalRealName || "ยังไม่ระบุ"}</strong>
                      {realNameChanged && <em>→ {draftRealName.trim() || "ยังไม่ระบุ"}</em>}
                    </div>
                  </div>

                  {notice && <div className="role-modal-notice">{notice}</div>}
                </div>

                <div className="role-modal-actions">
                  <button type="button" className="secondary" onClick={closeEditor} disabled={saving}>ยกเลิก</button>
                  <button type="button" className="primary" onClick={requestSave} disabled={!hasChanges || saving}>
                    ตรวจสอบการเปลี่ยนแปลง
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="role-modal-body">
                  <div className="role-confirm-box">
                    <span className="role-confirm-icon">!</span>
                    <h3>ยืนยันการเปลี่ยนข้อมูลผู้ใช้</h3>
                    <p>การเปลี่ยน Role มีผลทันที และบัญชี Team จะกรอกข้อมูลได้เฉพาะชื่อทีมที่แอดมินผูกไว้เท่านั้น</p>
                    <dl>
                      {roleChanged && (
                        <div>
                          <dt>Role</dt>
                          <dd>{ROLE_META[normalizeRole(editingUser.role)].label} → {ROLE_META[draftRole].label}</dd>
                        </div>
                      )}
                      {teamNameChanged && (
                        <div>
                          <dt>ชื่อทีม</dt>
                          <dd>{originalTeamName || "ยังไม่ผูก"} → {draftTeamName || "ยังไม่ผูก"}</dd>
                        </div>
                      )}
                      {realNameChanged && (
                        <div>
                          <dt>ชื่อจริง</dt>
                          <dd>{originalRealName || "ยังไม่ระบุ"} → {draftRealName.trim() || "ยังไม่ระบุ"}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  {notice && <div className="role-modal-notice">{notice}</div>}
                </div>
                <div className="role-modal-actions">
                  <button type="button" className="secondary" onClick={() => setConfirming(false)} disabled={saving}>ย้อนกลับ</button>
                  <button type="button" className="danger" onClick={saveChanges} disabled={saving}>
                    {saving ? "กำลังบันทึก..." : "ยืนยันและบันทึก"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
